import { useEffect, useState, Fragment } from "react";
import { toast } from "sonner";
import { Truck, MagnifyingGlass, PaperPlaneTilt, CheckCircle, XCircle, Warning, ArrowsClockwise, CaretDown, CaretRight, Package, Receipt } from "@phosphor-icons/react";
import Layout from "../components/Layout";
import api from "../lib/api";

const inp = { width: "100%", padding: "9px 11px", border: "1px solid #E2E8F0", borderRadius: 8, fontSize: 13 };

const statusStyle = (code) => {
  const map = {
    "3": { bg: "#DCFCE7", bd: "#BBF7D0", fg: "#166534" }, // Finished
    "2": { bg: "#DBEAFE", bd: "#BFDBFE", fg: "#1E40AF" }, // In Process
    "1": { bg: "#FEF3C7", bd: "#FDE68A", fg: "#92400E" }, // Not Started
  };
  const c = map[code] || { bg: "#F1F5F9", bd: "#E2E8F0", fg: "#475569" };
  return { fontSize: 10, fontWeight: 800, padding: "2px 8px", borderRadius: 999, background: c.bg, border: `1px solid ${c.bd}`, color: c.fg, whiteSpace: "nowrap" };
};

const stageStyle = (s) => {
  const map = {
    staged: { bg: "#FEF3C7", bd: "#FDE68A", fg: "#92400E", label: "Staged" },
    shipped: { bg: "#DCFCE7", bd: "#BBF7D0", fg: "#166534", label: "Shipped" },
    shipped_manual: { bg: "#DCFCE7", bd: "#BBF7D0", fg: "#166534", label: "Shipped (manual)" },
    partial: { bg: "#FFEDD5", bd: "#FED7AA", fg: "#9A3412", label: "Partial" },
    needs_manual: { bg: "#FEE2E2", bd: "#FECACA", fg: "#991B1B", label: "Needs manual PGI" },
  };
  const c = map[s] || { bg: "#F1F5F9", bd: "#E2E8F0", fg: "#475569", label: s || "—" };
  return { chip: { fontSize: 10, fontWeight: 800, padding: "2px 8px", borderRadius: 999, background: c.bg, border: `1px solid ${c.bd}`, color: c.fg, whiteSpace: "nowrap" }, label: c.label };
};

// ---- Staged Shipments (auto-staged from the app Dispatch Register) ----
function StagedShipments() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [expanded, setExpanded] = useState({});
  const [shippingInv, setShippingInv] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/sap/outbound/staged");
      setRows(r.data || []);
    } catch (e) { toast.error("Failed to load staged shipments"); setRows([]); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const scan = async () => {
    setScanning(true);
    try {
      const r = await api.post("/sap/outbound/scan");
      const d = r.data || {};
      if (d.new_staged > 0) toast.success(`Staged ${d.new_staged} new truck(s)`);
      else toast.info(`No new trucks to stage (scanned ${d.scanned} invoiced${d.skipped_no_so ? `, ${d.skipped_no_so} without a SAP order` : ""})`);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Scan failed"); }
    finally { setScanning(false); }
  };

  const shipTruck = async (inv) => {
    if (!window.confirm(`Post Goods Issue for invoice ${inv}?\n\nThis creates the Outbound Delivery and posts the Goods Issue in SAP (releases stock) for every item on this truck. This cannot be undone from here.`)) return;
    setShippingInv(inv);
    try {
      const r = await api.post(`/sap/outbound/staged/${encodeURIComponent(inv)}/ship`);
      if (r.data.status === "needs_manual") {
        toast.warning(`Truck ${inv} has partial-qty lines — post it manually in SAP, then click "Mark PGI Done".`);
      } else {
        const okCount = (r.data.results || []).filter((x) => x.ok).length;
        if (r.data.ok) toast.success(`Posted goods issue for truck ${inv} (${okCount} item(s))`);
        else toast.warning(`Truck ${inv}: ${okCount}/${(r.data.results || []).length} item(s) posted — see status`);
      }
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Post Goods Issue failed"); }
    finally { setShippingInv(null); }
  };

  const markShipped = async (inv) => {
    if (!window.confirm(`Confirm you have posted the Outbound Delivery for invoice ${inv} manually in SAP?\n\nThis just clears it from the pending list — it does NOT post anything to SAP.`)) return;
    setShippingInv(inv);
    try {
      await api.post(`/sap/outbound/staged/${encodeURIComponent(inv)}/mark-shipped`);
      toast.success(`Truck ${inv} marked as shipped`);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to mark shipped"); }
    finally { setShippingInv(null); }
  };

  const stagedCount = rows.filter((x) => x.status === "staged" || x.status === "needs_manual" || x.status === "partial").length;

  return (
    <div data-testid="sap-outbound-staged" style={{ marginBottom: 28 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10, flexWrap: "wrap", gap: 8 }}>
        <div>
          <h2 style={{ fontSize: 16, fontWeight: 800, margin: 0, color: "#0F172A", display: "flex", alignItems: "center", gap: 8 }}>
            <Package size={18} weight="bold" color="#0EA5E9" /> Staged Shipments
          </h2>
          <p style={{ fontSize: 12, color: "#64748B", margin: "3px 0 0" }}>
            Auto-staged the moment a truck is invoiced on the Dispatch Register (1 invoice = 1 truck). Review, then Post Goods Issue.
          </p>
        </div>
        <button data-testid="sap-outbound-scan" className="jde-btn" onClick={scan} disabled={scanning || loading}>
          <ArrowsClockwise size={15} weight="bold" style={{ marginRight: 6, verticalAlign: "-2px" }} />
          {scanning ? "Scanning…" : "Scan now"}
        </button>
      </div>

      <div style={{ fontSize: 12, color: "#64748B", marginBottom: 8 }}>
        {loading ? "Loading…" : `${rows.length} truck(s) · ${stagedCount} pending goods issue`}
      </div>

      <div style={{ border: "1px solid #E2E8F0", borderRadius: 12, overflow: "hidden", background: "#fff" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
          <thead>
            <tr style={{ background: "#F8FAFC", textAlign: "left", color: "#475569" }}>
              <th style={{ padding: "9px 10px", width: 30 }}></th>
              <th style={{ padding: "9px 10px" }}>Invoice #</th>
              <th style={{ padding: "9px 10px" }}>Customer</th>
              <th style={{ padding: "9px 10px" }}>Dispatch date</th>
              <th style={{ padding: "9px 10px" }}>SAP Sales Order(s)</th>
              <th style={{ padding: "9px 10px", textAlign: "right" }}>Items</th>
              <th style={{ padding: "9px 10px" }}>Status</th>
              <th style={{ padding: "9px 10px", textAlign: "right" }}>Action</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((t) => {
              const st = stageStyle(t.status);
              const open = !!expanded[t.inv_no];
              const isManual = t.has_partial || t.status === "needs_manual";
              const isShipped = t.status === "shipped" || t.status === "shipped_manual";
              return (
                <Fragment key={t.inv_no}>
                  <tr data-testid={`sap-staged-row-${t.inv_no}`} style={{ borderTop: "1px solid #F1F5F9" }}>
                    <td style={{ padding: "8px 10px", cursor: "pointer" }} onClick={() => setExpanded((e) => ({ ...e, [t.inv_no]: !e[t.inv_no] }))} data-testid={`sap-staged-expand-${t.inv_no}`}>
                      {open ? <CaretDown size={14} weight="bold" /> : <CaretRight size={14} weight="bold" />}
                    </td>
                    <td style={{ padding: "8px 10px", fontWeight: 700, color: "#0F172A" }}>{t.inv_no}</td>
                    <td style={{ padding: "8px 10px", color: "#334155", maxWidth: 260, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{t.customer_name || t.customer || "—"}</td>
                    <td style={{ padding: "8px 10px", color: "#64748B" }}>{t.dispatch_date || "—"}</td>
                    <td style={{ padding: "8px 10px", color: "#475569" }}>{(t.so_ids || []).join(", ") || "—"}</td>
                    <td style={{ padding: "8px 10px", textAlign: "right", color: "#0F172A", fontWeight: 700 }}>
                      {(t.lines || []).length}
                      {t.has_partial && (
                        <span data-testid={`sap-staged-partial-${t.inv_no}`} title="Contains partial-quantity lines"
                          style={{ marginLeft: 6, fontSize: 9, fontWeight: 800, padding: "1px 6px", borderRadius: 999, background: "#FFEDD5", border: "1px solid #FED7AA", color: "#9A3412" }}>PARTIAL</span>
                      )}
                      {(t.unmatched || []).length > 0 && (
                        <span data-testid={`sap-staged-unmatched-${t.inv_no}`} title={`${t.unmatched.length} shipped line(s) not matched to SAP`}
                          style={{ marginLeft: 6, color: "#DC2626", cursor: "help", verticalAlign: "-2px" }}>
                          <Warning size={13} weight="fill" />
                        </span>
                      )}
                    </td>
                    <td style={{ padding: "8px 10px" }}><span style={st.chip}>{st.label}</span></td>
                    <td style={{ padding: "8px 10px", textAlign: "right" }}>
                      {isShipped ? (
                        <span style={{ color: "#16A34A", display: "inline-flex", alignItems: "center", gap: 4, fontSize: 12, fontWeight: 700 }}>
                          <CheckCircle size={15} weight="fill" /> {t.status === "shipped_manual" ? "Shipped (manual)" : "Shipped"}
                        </span>
                      ) : isManual ? (
                        <button data-testid={`sap-staged-markdone-${t.inv_no}`} className="jde-btn"
                          title="This truck has partial-quantity lines. Post its Outbound Delivery in SAP (edit qty per line), then click here to clear it."
                          onClick={() => markShipped(t.inv_no)} disabled={shippingInv === t.inv_no}>
                          <CheckCircle size={14} weight="bold" style={{ marginRight: 5, verticalAlign: "-2px" }} />
                          {shippingInv === t.inv_no ? "Saving…" : "Mark PGI Done"}
                        </button>
                      ) : (
                        <button data-testid={`sap-staged-ship-${t.inv_no}`} className="jde-btn jde-btn-primary"
                          onClick={() => shipTruck(t.inv_no)} disabled={shippingInv === t.inv_no || (t.lines || []).length === 0}>
                          <PaperPlaneTilt size={14} weight="bold" style={{ marginRight: 5, verticalAlign: "-2px" }} />
                          {shippingInv === t.inv_no ? "Posting…" : "Post Goods Issue"}
                        </button>
                      )}
                    </td>
                  </tr>
                  {open && (
                    <tr key={`${t.inv_no}-items`} style={{ background: "#F8FAFC" }}>
                      <td colSpan={8} style={{ padding: "0 10px 10px 40px" }}>
                        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
                          <thead>
                            <tr style={{ textAlign: "left", color: "#64748B" }}>
                              <th style={{ padding: "6px 8px" }}>Customer PO</th>
                              <th style={{ padding: "6px 8px" }}>Item</th>
                              <th style={{ padding: "6px 8px" }}>SAP SO</th>
                              <th style={{ padding: "6px 8px", textAlign: "right" }}>Ship Qty</th>
                              <th style={{ padding: "6px 8px", textAlign: "right" }}>SAP Open</th>
                              <th style={{ padding: "6px 8px" }}>Type</th>
                              <th style={{ padding: "6px 8px" }}>Posted</th>
                            </tr>
                          </thead>
                          <tbody>
                            {(t.lines || []).map((l, i) => (
                              <tr key={`${l.custpo}-${l.icode}-${i}`} style={{ borderTop: "1px solid #EEF2F6" }}>
                                <td style={{ padding: "5px 8px", color: "#334155" }}>{l.custpo || "—"}</td>
                                <td style={{ padding: "5px 8px", fontWeight: 600, color: "#0F172A" }}>{l.icode}</td>
                                <td style={{ padding: "5px 8px", color: "#64748B" }}>{l.so_id}</td>
                                <td style={{ padding: "5px 8px", textAlign: "right", fontWeight: 700, color: "#0F172A" }}>{l.ship_qty}</td>
                                <td style={{ padding: "5px 8px", textAlign: "right", color: "#64748B" }}>{l.open_qty}</td>
                                <td style={{ padding: "5px 8px" }}>
                                  {l.is_partial
                                    ? <span style={{ fontSize: 9, fontWeight: 800, padding: "1px 6px", borderRadius: 999, background: "#FFEDD5", border: "1px solid #FED7AA", color: "#9A3412" }}>PARTIAL</span>
                                    : <span style={{ fontSize: 9, fontWeight: 800, padding: "1px 6px", borderRadius: 999, background: "#DCFCE7", border: "1px solid #BBF7D0", color: "#166534" }}>FULL</span>}
                                </td>
                                <td style={{ padding: "5px 8px" }}>{l.shipped ? <span style={{ color: "#16A34A" }}>✓</span> : "—"}</td>
                              </tr>
                            ))}
                            {(t.unmatched || []).map((u, i) => (
                              <tr key={`u-${i}`} style={{ borderTop: "1px solid #EEF2F6", background: "#FEF2F2" }}>
                                <td style={{ padding: "5px 8px", color: "#991B1B" }}>{u.custpo || "—"}</td>
                                <td style={{ padding: "5px 8px", fontWeight: 600, color: "#991B1B" }}>{u.icode}</td>
                                <td colSpan={5} style={{ padding: "5px 8px", color: "#DC2626" }}>Not staged — {u.reason}</td>
                              </tr>
                            ))}
                            {(t.lines || []).length === 0 && (t.unmatched || []).length === 0 && (
                              <tr><td colSpan={7} style={{ padding: 10, color: "#94A3B8" }}>No shipped lines resolved.</td></tr>
                            )}
                          </tbody>
                        </table>
                      </td>
                    </tr>
                  )}
                </Fragment>
              );
            })}
            {!loading && rows.length === 0 && (
              <tr><td colSpan={8} style={{ padding: 24, textAlign: "center", color: "#94A3B8" }}>
                <Warning size={17} weight="bold" style={{ verticalAlign: "-3px", marginRight: 6 }} />
                No staged trucks yet. Click "Scan now" to stage invoiced trucks whose POs have a SAP Sales Order.
              </td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ---- Dispatch Plans → SAP Outbound (app-driven create + release; DispatchNumber = Voc_No) ----
function DispatchPlanPush() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(null);
  const [detail, setDetail] = useState({});

  const load = async () => {
    setLoading(true);
    try { const r = await api.get("/sap/outbound/dispatch-plans?limit=100"); setRows(r.data || []); }
    catch (e) { toast.error("Failed to load dispatch plans"); setRows([]); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const showDetail = async (voc) => {
    try { const r = await api.get(`/sap/outbound/dispatch-plans/${voc}`); setDetail((d) => ({ ...d, [voc]: r.data })); }
    catch (e) { toast.error("Failed to load plan detail"); }
  };

  const preview = async (voc) => {
    setBusy(`prev-${voc}`);
    try {
      const r = await api.get(`/sap/outbound/dispatch-plans/${voc}/resolve`);
      setDetail((d) => ({ ...d, [voc]: { resolve: r.data } }));
      const n = (r.data.lines || []).length, u = (r.data.unmatched || []).length;
      toast.info(`Plan ${voc}: ${n} line(s) mapped to SAP${u ? `, ${u} unmatched` : ""}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Preview failed"); }
    finally { setBusy(null); }
  };

  const push = async (voc, release) => {
    const msg = release
      ? `Push AND RELEASE plan ${voc} to SAP?\n\nThis creates the Outbound Delivery(ies) AND posts Goods Issue (releases stock). Cannot be undone from here.`
      : `Push plan ${voc} to SAP as DRAFT (In Preparation)?\n\nThis creates the Outbound Delivery(ies) and stamps DispatchNumber=${voc}. No goods issue yet — you review in SAP, then Release.`;
    if (!window.confirm(msg)) return;
    setBusy(`push-${voc}`);
    try {
      const r = await api.post(`/sap/outbound/dispatch-plans/${voc}/push?release=${release ? "true" : "false"}`);
      setDetail((d) => ({ ...d, [voc]: { push: r.data } }));
      if (r.data.ok) toast.success(`Plan ${voc} pushed${release ? " + released" : " (draft)"} — ${(r.data.deliveries || []).length} delivery item(s)`);
      else toast.warning(`Plan ${voc}: partial/failed — see detail`);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Push failed"); }
    finally { setBusy(null); }
  };

  const release = async (voc) => {
    if (!window.confirm(`Release plan ${voc} in SAP (post Goods Issue on its drafts)?\n\nThis releases stock and cannot be undone from here.`)) return;
    setBusy(`rel-${voc}`);
    try {
      const r = await api.post(`/sap/outbound/dispatch-plans/${voc}/release`);
      setDetail((d) => ({ ...d, [voc]: { push: r.data } }));
      if (r.data.ok) toast.success(`Plan ${voc} released in SAP`);
      else toast.warning(`Plan ${voc}: not fully released — see detail`);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Release failed"); }
    finally { setBusy(null); }
  };

  const invPreview = async (voc) => {
    setBusy(`invp-${voc}`);
    try {
      const r = await api.post(`/sap/outbound/dispatch-plans/${voc}/invoice/preview`);
      setDetail((d) => ({ ...d, [voc]: { invoice: r.data } }));
      if (r.data.reason) toast.info(`Plan ${voc}: ${r.data.reason}`);
      else toast.info(`Plan ${voc}: invoice dry-run done — see detail`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Invoice preview failed"); }
    finally { setBusy(null); }
  };

  const invCreate = async (voc) => {
    if (!window.confirm(`Create the SAP GST/customer invoice for plan ${voc}?\n\nThis posts a real invoice (itemized, SAP-priced), stamps the Export Invoice No, and releases it. Cannot be undone from here.`)) return;
    setBusy(`invc-${voc}`);
    try {
      const r = await api.post(`/sap/outbound/dispatch-plans/${voc}/invoice?release=true`);
      setDetail((d) => ({ ...d, [voc]: { invoice: r.data } }));
      if (r.data.ok) toast.success(`Plan ${voc}: invoice created${r.data.export_no ? ` (Export ${r.data.export_no})` : ""}`);
      else toast.warning(`Plan ${voc}: invoice not fully created — see detail`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Invoice create failed"); }
    finally { setBusy(null); }
  };

  const chip = (s) => {
    const m = { released: { bg: "#DCFCE7", bd: "#BBF7D0", fg: "#166534" },
                pushed: { bg: "#DBEAFE", bd: "#BFDBFE", fg: "#1E40AF" },
                not_pushed: { bg: "#F1F5F9", bd: "#E2E8F0", fg: "#475569" } }[s] || {};
    return { fontSize: 10, fontWeight: 800, padding: "2px 8px", borderRadius: 999, background: m.bg, border: `1px solid ${m.bd}`, color: m.fg };
  };

  return (
    <div data-testid="sap-outbound-plans" style={{ marginBottom: 28 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10, flexWrap: "wrap", gap: 8 }}>
        <div>
          <h2 style={{ fontSize: 16, fontWeight: 800, margin: 0, color: "#0F172A", display: "flex", alignItems: "center", gap: 8 }}>
            <Truck size={18} weight="bold" color="#0EA5E9" /> Dispatch Plans → SAP Outbound
          </h2>
          <p style={{ fontSize: 12, color: "#64748B", margin: "3px 0 0" }}>
            Push a dispatch plan to SAP: creates the Outbound Delivery(ies), stamps <b>DispatchNumber = Plan #</b>. Push leaves them <b>In Preparation</b> (draft) for review; then <b>Release</b> posts the Goods Issue.
          </p>
        </div>
        <button data-testid="sap-plans-reload" className="jde-btn" onClick={load} disabled={loading}>
          <ArrowsClockwise size={15} weight="bold" style={{ marginRight: 6, verticalAlign: "-2px" }} />{loading ? "Loading…" : "Reload"}
        </button>
      </div>

      <div style={{ border: "1px solid #E2E8F0", borderRadius: 12, overflowX: "auto", background: "#fff" }}>
        <table style={{ width: "100%", minWidth: 980, borderCollapse: "collapse", fontSize: 13 }}>
          <thead>
            <tr style={{ background: "#F8FAFC", textAlign: "left", color: "#475569" }}>
              <th style={{ padding: "9px 10px" }}>Plan # (Voc)</th>
              <th style={{ padding: "9px 10px" }}>Export Inv #</th>
              <th style={{ padding: "9px 10px" }}>Customer</th>
              <th style={{ padding: "9px 10px" }}>Date</th>
              <th style={{ padding: "9px 10px" }}>SAP delivery ID(s)</th>
              <th style={{ padding: "9px 10px" }}>Status</th>
              <th style={{ padding: "9px 10px", textAlign: "right" }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((p) => {
              const d = detail[p.voc_no];
              const pushDoc = d?.push || (d?.sap_deliveries ? d : null);
              const deliveries = pushDoc?.deliveries || d?.push?.deliveries || [];
              return (
                <Fragment key={p.voc_no}>
                  <tr data-testid={`sap-plan-row-${p.voc_no}`} style={{ borderTop: "1px solid #F1F5F9" }}>
                    <td style={{ padding: "8px 10px", fontWeight: 700, color: "#0F172A" }}>{p.voc_no}</td>
                    <td style={{ padding: "8px 10px", color: "#334155" }}>{p.inv_no || "—"}</td>
                    <td style={{ padding: "8px 10px", color: "#334155", maxWidth: 220, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{p.customer || "—"}</td>
                    <td style={{ padding: "8px 10px", color: "#64748B" }}>{p.dispatch_date || "—"}</td>
                    <td style={{ padding: "8px 10px", color: "#475569", fontSize: 12 }}>{(p.sap_delivery_ids || []).join(", ") || "—"}</td>
                    <td style={{ padding: "8px 10px" }}><span style={chip(p.push_status)}>{p.push_status.replace("_", " ")}</span></td>
                    <td style={{ padding: "8px 10px", textAlign: "right", whiteSpace: "nowrap" }}>
                      <button data-testid={`sap-plan-preview-${p.voc_no}`} className="jde-btn" style={{ marginRight: 6 }} onClick={() => preview(p.voc_no)} disabled={busy === `prev-${p.voc_no}`}>
                        {busy === `prev-${p.voc_no}` ? "…" : "Preview"}
                      </button>
                      <button data-testid={`sap-plan-push-${p.voc_no}`} className="jde-btn jde-btn-primary" style={{ marginRight: 6 }} onClick={() => push(p.voc_no, false)} disabled={busy === `push-${p.voc_no}`}>
                        <PaperPlaneTilt size={13} weight="bold" style={{ marginRight: 4, verticalAlign: "-2px" }} />
                        {busy === `push-${p.voc_no}` ? "Pushing…" : "Push (draft)"}
                      </button>
                      <button data-testid={`sap-plan-release-${p.voc_no}`} className="jde-btn" onClick={() => release(p.voc_no)} disabled={busy === `rel-${p.voc_no}` || p.push_status === "not_pushed"}>
                        {busy === `rel-${p.voc_no}` ? "…" : "Release"}
                      </button>
                      <button data-testid={`sap-plan-inv-preview-${p.voc_no}`} className="jde-btn" style={{ marginLeft: 6 }} onClick={() => invPreview(p.voc_no)} disabled={busy === `invp-${p.voc_no}` || p.push_status === "not_pushed"}>
                        {busy === `invp-${p.voc_no}` ? "…" : "Preview Inv"}
                      </button>
                      <button data-testid={`sap-plan-inv-create-${p.voc_no}`} className="jde-btn jde-btn-primary" style={{ marginLeft: 6 }} onClick={() => invCreate(p.voc_no)} disabled={busy === `invc-${p.voc_no}` || p.push_status !== "released"}>
                        <Receipt size={13} weight="bold" style={{ marginRight: 4, verticalAlign: "-2px" }} />
                        {busy === `invc-${p.voc_no}` ? "Invoicing…" : "Create Inv"}
                      </button>
                    </td>
                  </tr>
                  {d && (
                    <tr style={{ background: "#F8FAFC" }}>
                      <td colSpan={7} style={{ padding: "6px 10px 12px 20px" }}>
                        {d.resolve && (
                          <div data-testid={`sap-plan-resolve-${p.voc_no}`} style={{ fontSize: 12, color: "#334155" }}>
                            <b>Preview</b> — SOs: {(d.resolve.so_ids || []).join(", ") || "none"}
                            {(d.resolve.lines || []).map((l, i) => (
                              <div key={i} style={{ color: "#475569" }}>• {l.icode} · SO {l.so_id} · ship {l.ship_qty}/{l.open_qty} · {l.is_partial ? "PARTIAL" : "FULL"}</div>
                            ))}
                            {(d.resolve.unmatched || []).map((u, i) => (
                              <div key={`u${i}`} style={{ color: "#DC2626" }}>✕ {u.icode} — {u.reason}</div>
                            ))}
                          </div>
                        )}
                        {deliveries.length > 0 && (
                          <div data-testid={`sap-plan-deliveries-${p.voc_no}`} style={{ fontSize: 12, color: "#334155" }}>
                            <b>SAP deliveries</b>
                            {deliveries.map((x, i) => (
                              <div key={i} style={{ color: x.released ? "#166534" : "#475569" }}>
                                • {x.icode} · SO {x.so_id} · {x.mode} · qty {x.qty} · delivery {x.sap_delivery_id || "—"} · stamped {x.stamped ? "✓" : "✗"} · released {x.released ? "✓" : "✗"}
                                {(x.create_msg || x.confirm_msg || x.release_msg) ? ` · ${x.create_msg || x.confirm_msg || x.release_msg}` : ""}
                              </div>
                            ))}
                          </div>
                        )}
                        {d.invoice && (
                          <div data-testid={`sap-plan-invoice-${p.voc_no}`} style={{ fontSize: 12, color: "#334155", marginTop: 6 }}>
                            <b>Invoice {d.invoice.dry_run ? "(dry-run)" : ""}</b>
                            {d.invoice.reason ? <span style={{ color: "#DC2626" }}> — {d.invoice.reason}</span> : null}
                            {d.invoice.export_no ? <span> · Export No {d.invoice.export_no}</span> : null}
                            {(d.invoice.results || []).map((x, i) => (
                              <div key={i} style={{ color: x.ok ? "#166534" : "#B45309" }}>
                                • delivery {x.delivery_id} · {x.items} item(s) · {x.stage} · ok {x.ok ? "✓" : "✗"}
                                {x.invoice?.invoice_id ? ` · invoice ${x.invoice.invoice_id}` : ""}
                                {x.readback?.ExportInvoiceno_KUT ? ` · stamped Export ${x.readback.ExportInvoiceno_KUT}` : ""}
                                {(x.fault || x.reason) ? ` · ${x.fault || x.reason}` : ""}
                                {(x.notes || []).slice(0, 2).map((n, j) => <div key={j} style={{ color: "#94A3B8", paddingLeft: 12 }}>– {n}</div>)}
                              </div>
                            ))}
                          </div>
                        )}
                      </td>
                    </tr>
                  )}
                </Fragment>
              );
            })}
            {!loading && rows.length === 0 && (
              <tr><td colSpan={7} style={{ padding: 22, textAlign: "center", color: "#94A3B8" }}>No dispatch plans found in the register snapshot.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default function SapOutbound() {
  const [so, setSo] = useState("");
  const [rows, setRows] = useState([]);
  const [sel, setSel] = useState({});
  const [loading, setLoading] = useState(false);
  const [shipping, setShipping] = useState(false);
  const [results, setResults] = useState(null);
  const [conn, setConn] = useState(null);

  useEffect(() => {
    api.get("/sap/outbound/status").then((r) => setConn(r.data)).catch(() => setConn({ ok: false }));
    load("");
  }, []);

  const load = async (soId) => {
    setLoading(true); setResults(null); setSel({});
    try {
      const r = await api.get(`/sap/outbound/requests?limit=60${soId ? `&so=${encodeURIComponent(soId)}` : ""}`);
      setRows(r.data || []);
      if (soId && (r.data || []).length === 0) toast.info(`No delivery requests found for Sales Order ${soId}`);
    } catch (e) { toast.error("Failed to load delivery requests"); setRows([]); }
    finally { setLoading(false); }
  };

  const shippable = rows.filter((x) => x.shippable);
  const selectedIds = Object.keys(sel).filter((k) => sel[k]);
  const toggle = (id) => setSel((s) => ({ ...s, [id]: !s[id] }));
  const toggleAll = () => {
    if (selectedIds.length === shippable.length) setSel({});
    else setSel(Object.fromEntries(shippable.map((x) => [x.object_id, true])));
  };

  const ship = async () => {
    if (selectedIds.length === 0) { toast.error("Select at least one open item"); return; }
    if (!window.confirm(`Post Goods Issue for ${selectedIds.length} item(s)?\n\nThis creates the Outbound Delivery and posts the Goods Issue in SAP (releases stock). This cannot be undone from here.`)) return;
    setShipping(true); setResults(null);
    try {
      const r = await api.post("/sap/outbound/ship", { object_ids: selectedIds, auto_release: true });
      setResults(r.data.results || []);
      const okCount = (r.data.results || []).filter((x) => x.ok).length;
      if (r.data.ok) toast.success(`Posted goods issue for ${okCount} item(s)`);
      else toast.warning(`${okCount}/${selectedIds.length} succeeded — see results below`);
      if (so) load(so); else load("");
    } catch (e) { toast.error(e?.response?.data?.detail || "Ship failed"); }
    finally { setShipping(false); }
  };

  const resultFor = (id) => (results || []).find((x) => x.object_id === id);

  return (
    <Layout breadcrumb={["Setup", "SAP Outbound"]}>
      <div data-testid="sap-outbound-page" style={{ maxWidth: 1800, margin: "0 auto", padding: "8px 4px 40px" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 4 }}>
          <Truck size={22} weight="bold" color="#0EA5E9" />
          <h1 style={{ fontSize: 22, fontWeight: 800, margin: 0, color: "#0F172A" }}>SAP Outbound Deliveries</h1>
          {conn && (
            <span data-testid="sap-outbound-conn" style={{ ...statusStyle(conn.ok ? "3" : "1"), marginLeft: 6 }}>
              {conn.ok ? `Connected · ${conn.total_requests?.toLocaleString?.() || conn.total_requests} requests` : "Not connected"}
            </span>
          )}
        </div>
        <p style={{ fontSize: 13, color: "#475569", margin: "0 0 16px" }}>
          Find the SAP delivery requests for a Sales Order (created from your PO via SAP Push), then
          <b> Post Goods Issue</b> — SAP creates the Outbound Delivery, posts the goods issue and releases it.
        </p>

        <DispatchPlanPush />

        <StagedShipments />

        <h2 style={{ fontSize: 16, fontWeight: 800, margin: "0 0 4px", color: "#0F172A", display: "flex", alignItems: "center", gap: 8 }}>
          <MagnifyingGlass size={17} weight="bold" color="#64748B" /> Manual — by Sales Order
        </h2>
        <p style={{ fontSize: 12, color: "#64748B", margin: "0 0 12px" }}>
          Search a SAP Sales Order ID and post goods issue for individual delivery-request items.
        </p>

        <div style={{ display: "flex", gap: 10, marginBottom: 16, alignItems: "center", flexWrap: "wrap" }}>
          <div style={{ position: "relative", flex: 1, minWidth: 240 }}>
            <MagnifyingGlass size={15} weight="bold" color="#94A3B8" style={{ position: "absolute", left: 11, top: "50%", transform: "translateY(-50%)" }} />
            <input data-testid="sap-outbound-so" style={{ ...inp, paddingLeft: 32 }} value={so}
              onChange={(e) => setSo(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load(so.trim())}
              placeholder="SAP Sales Order ID (e.g. 11181) — Enter to search" />
          </div>
          <button data-testid="sap-outbound-search" className="jde-btn jde-btn-primary" onClick={() => load(so.trim())} disabled={loading}>Search</button>
          <button data-testid="sap-outbound-open" className="jde-btn" onClick={() => { setSo(""); load(""); }} disabled={loading}>Show open</button>
        </div>

        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
          <div style={{ fontSize: 12, color: "#64748B" }}>
            {loading ? "Loading from SAP…" : `${rows.length} item(s) · ${shippable.length} shippable`}
          </div>
          <button data-testid="sap-outbound-ship" className="jde-btn jde-btn-primary" onClick={ship}
            disabled={shipping || selectedIds.length === 0}>
            <PaperPlaneTilt size={15} weight="bold" style={{ marginRight: 6, verticalAlign: "-2px" }} />
            {shipping ? "Posting…" : `Post Goods Issue (${selectedIds.length})`}
          </button>
        </div>

        <div style={{ border: "1px solid #E2E8F0", borderRadius: 12, overflow: "hidden", background: "#fff" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
            <thead>
              <tr style={{ background: "#F8FAFC", textAlign: "left", color: "#475569" }}>
                <th style={{ padding: "9px 10px", width: 34 }}>
                  <input type="checkbox" data-testid="sap-outbound-selectall" checked={shippable.length > 0 && selectedIds.length === shippable.length} onChange={toggleAll} />
                </th>
                <th style={{ padding: "9px 10px" }}>SAP Product</th>
                <th style={{ padding: "9px 10px" }}>Description</th>
                <th style={{ padding: "9px 10px", textAlign: "right" }}>Open Qty</th>
                <th style={{ padding: "9px 10px", textAlign: "right" }}>Requested</th>
                <th style={{ padding: "9px 10px" }}>Status</th>
                <th style={{ padding: "9px 10px" }}>Result</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((x) => {
                const res = resultFor(x.object_id);
                return (
                  <tr key={x.object_id} data-testid={`sap-outbound-row-${x.object_id}`} style={{ borderTop: "1px solid #F1F5F9" }}>
                    <td style={{ padding: "8px 10px" }}>
                      <input type="checkbox" data-testid={`sap-outbound-chk-${x.object_id}`} disabled={!x.shippable}
                        checked={!!sel[x.object_id]} onChange={() => toggle(x.object_id)} />
                    </td>
                    <td style={{ padding: "8px 10px", fontWeight: 700, color: "#0F172A" }}>{x.sap_product || "—"}</td>
                    <td style={{ padding: "8px 10px", color: "#64748B", maxWidth: 320, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{x.description}</td>
                    <td style={{ padding: "8px 10px", textAlign: "right", fontWeight: 700, color: x.open_qty > 0 ? "#0F172A" : "#94A3B8" }}>{x.open_qty} {x.unit}</td>
                    <td style={{ padding: "8px 10px", textAlign: "right", color: "#64748B" }}>{x.requested_qty} {x.unit}</td>
                    <td style={{ padding: "8px 10px" }}><span style={statusStyle(x.status_code)}>{x.status}</span></td>
                    <td style={{ padding: "8px 10px" }}>
                      {res ? (res.ok
                        ? <span style={{ color: "#16A34A", display: "inline-flex", alignItems: "center", gap: 4 }}><CheckCircle size={15} weight="fill" /> Shipped</span>
                        : <span title={res.message || res.reason} style={{ color: "#DC2626", display: "inline-flex", alignItems: "center", gap: 4 }}><XCircle size={15} weight="fill" /> {(res.message || res.reason || "Failed").slice(0, 40)}</span>)
                        : null}
                    </td>
                  </tr>
                );
              })}
              {!loading && rows.length === 0 && (
                <tr><td colSpan={7} style={{ padding: 28, textAlign: "center", color: "#94A3B8" }}>
                  <Warning size={18} weight="bold" style={{ verticalAlign: "-3px", marginRight: 6 }} />
                  No delivery requests. Search a Sales Order ID or click "Show open".
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </Layout>
  );
}
